// Mobile Cart Item Component
// Simplified cart item for mobile devices

import type { CartItem as CartItemType } from '@/types';
import styles from './MobileCartItem.module.css';

interface MobileCartItemProps {
  item: CartItemType;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemove: (itemId: string) => void;
}

export function MobileCartItem({ item, onUpdateQuantity, onRemove }: MobileCartItemProps) {
  const product = item.product;

  // Format price
  const formatPrice = (price: number) => {
    return `$${Math.round(price).toLocaleString()}`;
  };

  // Get display image
  const displayImage = product.featuredImageUrl || product.imageUrls?.[0] || '';

  // Build variant display text
  const variantText = item.variantAttributes
    ? Object.entries(item.variantAttributes)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')
    : null;

  return (
    <div className={styles.cartItem}>
      {/* Product Image */}
      <div className={styles.imageContainer}>
        {displayImage ? (
          <img src={displayImage} alt={product.title} className={styles.image} />
        ) : (
          <div className={styles.imagePlaceholder} />
        )}
      </div>

      {/* Product Info */}
      <div className={styles.productInfo}>
        <h3 className={styles.productTitle}>{product.title}</h3>
        {variantText && <p className={styles.variantText}>{variantText}</p>}
        <p className={styles.price}>{formatPrice(item.subtotal)}</p>

        {/* Quantity Controls and Remove */}
        <div className={styles.actions}>
          <div className={styles.quantityControls}>
            <button
              className={styles.quantityButton}
              onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
              disabled={item.quantity <= 1}
              aria-label="Decrease quantity"
            >
              −
            </button>
            <span className={styles.quantity}>{item.quantity}</span>
            <button
              className={styles.quantityButton}
              onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>

          <button
            className={styles.removeButton}
            onClick={() => onRemove(item.id)}
            aria-label="Remove item"
          >
            移除
          </button>
        </div>
      </div>
    </div>
  );
}
